/**
 * 書類・印刷（役職者向け）
 * 陸上（管理者）が配信したファイル（PDF/画像）を受け取り、船内プリンターで印刷する。
 * 役職者（書類権限保持者）は、証明書以外の任意ファイル（PDF/PNG/JPEG）を陸上へ送ることもできる。
 */
import React, { useState, useEffect, useCallback } from "react";
import { AppHeader } from "../components/AppHeader";
import { apiFetch, apiUpload, getAccessToken, isPersonalDevice } from "../services/api";
import { companionPrint } from "../services/alcohol-companion";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

interface Delivery {
  id: string;
  title: string;
  file_name: string | null;
  file_mime: string | null;
  note: string | null;
  vessel_name: string | null;
  recipient_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  printed_at: string | null;
}
interface ShoreAdmin { id: string; user_name: string }

const fmtDt = (v?: string | null) => (v ? String(v).slice(0, 16).replace("T", " ") : "-");
const isImage = (m?: string | null) => !!m && m.startsWith("image/");

export const DocumentsPage: React.FC = () => {
  const [docs, setDocs] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shoreAdmins, setShoreAdmins] = useState<ShoreAdmin[]>([]);

  // 送信シート（役職者→陸上へ任意ファイル）
  const [sendOpen, setSendOpen] = useState(false);
  const [sendFile, setSendFile] = useState<File | null>(null);
  const [sendTitle, setSendTitle] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [sendSel, setSendSel] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  let perms: Record<string, boolean> = {};
  try { perms = JSON.parse(localStorage.getItem("catms_permissions") ?? "{}"); } catch { /* noop */ }
  const canSend = !!(perms.admin_documents || perms.print_documents);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch<{ data: Delivery[] }>("/documents/inbox");
      setDocs(res.data ?? []);
    } catch {
      setError("書類の取得に失敗しました。通信環境をご確認ください。");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiFetch<{ data: ShoreAdmin[] }>("/documents/shore-admins").then(r => setShoreAdmins(r.data ?? [])).catch(() => {});
  }, []);

  const fileUrl = (d: Delivery) => {
    const abs = API_BASE.startsWith("http") ? API_BASE : window.location.origin + API_BASE;
    return `${abs}/documents/${d.id}/file?token=${encodeURIComponent(getAccessToken() ?? "")}`;
  };

  const openAndPrint = async (d: Delivery) => {
    // 開封／印刷済み記録（失敗しても開くのは続行）
    apiFetch(`/documents/${d.id}/printed`, { method: "POST" }).catch(() => {});
    setDocs(prev => prev.map(x => x.id === d.id ? { ...x, printed_at: x.printed_at ?? new Date().toISOString() } : x));
    const url = fileUrl(d);
    const native = !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();
    // PDFは Companion 経由で船内プリンターへ。画像はビューアで表示（印刷は表示先メニューから）。
    // 個人スマホはCompanionが無いため試行せず（接続タイムアウト待ちを避ける）ブラウザ表示→端末の印刷機能を使う。
    if (native && !isPersonalDevice() && !isImage(d.file_mime)) {
      const ok = await companionPrint(url, d.title || "書類");
      if (ok) return;
    }
    window.open(url, "_blank");
  };

  // 受信者が自分の一覧から消す（陸上の記録は残る）。楽観的に即消して裏で反映。
  const dismiss = async (d: Delivery) => {
    if (!window.confirm(`「${d.title}」をこの一覧から削除しますか？\n（陸上側の記録は残ります）`)) return;
    setDocs(prev => prev.filter(x => x.id !== d.id));
    try { await apiFetch(`/documents/${d.id}/dismiss`, { method: "POST" }); }
    catch { alert("削除に失敗しました。通信環境をご確認ください。"); load(); }
  };

  const doSend = async () => {
    if (!sendFile) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", sendFile);
      if (sendTitle.trim()) fd.append("title", sendTitle.trim());
      if (sendNote.trim()) fd.append("note", sendNote.trim());
      sendSel.forEach(id => fd.append("recipient_user_ids", id));
      const res = await apiUpload<{ data: { notified: number } }>("/documents/submit", fd);
      setSendOpen(false); setSendFile(null); setSendTitle(""); setSendNote(""); setSendSel([]);
      setError("");
      alert(`陸上へ送りました（管理者${res?.data?.notified ?? 0}名へ通知）`);
    } catch (e) {
      alert(e instanceof Error ? e.message : "送信に失敗しました");
    }
    setSending(false);
  };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#f8fafc" }}>
      {/* ヘッダー */}
      <AppHeader
        title="書類・印刷"
        right={
          <button onClick={load} style={{ flexShrink: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, padding: "7px 13px", fontSize: 12.5, fontWeight: 700, color: "#64748b", cursor: "pointer" }}>更新</button>
        }
      />

      <div className="app-scroll-bottom-safe" style={{ flex: 1, overflowY: "auto", padding: "16px 16px 32px" }}>
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: "10px 14px", fontSize: 12, color: "#1d4ed8", lineHeight: 1.7, marginBottom: 14 }}>
          🖨 「開いて印刷」→ 開いた画面のメニューから<strong>印刷</strong>を選ぶと、船内のプリンターに出力できます。
        </div>

        {canSend && (
          <button onClick={() => setSendOpen(true)}
            style={{ width: "100%", marginBottom: 14, padding: "13px 0", borderRadius: 12, border: "1.5px solid #c7d2fe", background: "#eef2ff", color: "#4338ca", fontSize: 14.5, fontWeight: 800, cursor: "pointer" }}>
            📤 ファイルを陸上へ送る（PDF/画像）
          </button>
        )}

        {loading && <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>読み込み中…</div>}
        {error && <div style={{ textAlign: "center", color: "#dc2626", padding: 20, fontSize: 13 }}>{error}</div>}
        {!loading && !error && docs.length === 0 && (
          <div style={{ textAlign: "center", color: "#94a3b8", padding: 50 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>📄</div>
            <div style={{ fontSize: 14 }}>届いている書類はありません</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>陸上から配信されるとここに表示されます</div>
          </div>
        )}

        {docs.map(d => (
          <div key={d.id} style={{ background: "white", borderRadius: 14, padding: "14px 16px", marginBottom: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderLeft: `4px solid ${d.printed_at ? "#cbd5e1" : "#0ea5e9"}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ fontSize: 24, flexShrink: 0 }}>{isImage(d.file_mime) ? "🖼" : "📄"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: "#0f172a" }}>{d.title}</span>
                  {d.printed_at ? (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", background: "#f1f5f9", borderRadius: 999, padding: "1px 8px" }}>✓ 確認済み</span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 999, padding: "1px 8px" }}>新着</span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 3 }}>
                  {d.created_by_name && <span>{d.created_by_name} から</span>}
                  <span style={{ marginLeft: 8 }}>{fmtDt(d.created_at)}</span>
                  {d.vessel_name && <span style={{ marginLeft: 8 }}>宛先: {d.vessel_name}</span>}
                </div>
                {d.note && (
                  <div style={{ fontSize: 12.5, color: "#475569", marginTop: 6, background: "#f8fafc", borderRadius: 8, padding: "7px 10px" }}>
                    {d.note}
                  </div>
                )}
              </div>
            </div>
            {/* 画像はインラインでサムネイル表示（ブラウザ非搭載の管理端末でも中身が見える） */}
            {isImage(d.file_mime) && (
              <img src={fileUrl(d)} alt={d.title}
                onClick={() => window.open(fileUrl(d), "_blank")}
                style={{ display: "block", width: "100%", maxHeight: 260, objectFit: "contain", marginTop: 10, borderRadius: 10, border: "1px solid #e2e8f0", background: "#f1f5f9", cursor: "pointer" }} />
            )}
            <button
              onClick={() => openAndPrint(d)}
              style={{ width: "100%", marginTop: 12, padding: "13px 0", borderRadius: 12, border: "none", background: "linear-gradient(145deg,#0057d9,#0369a1)", color: "white", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 14px rgba(0,87,217,0.3)" }}
            >
              {isImage(d.file_mime) ? "🔍 開く" : "🖨 開いて印刷"}
            </button>
            <button
              onClick={() => dismiss(d)}
              style={{ width: "100%", marginTop: 8, padding: "10px 0", borderRadius: 12, border: "1px solid #fecaca", background: "#fff", color: "#dc2626", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
            >
              🗑 一覧から削除
            </button>
          </div>
        ))}
      </div>

      {/* 送信シート（役職者→陸上へ任意ファイル） */}
      {sendOpen && (
        <div onClick={() => !sending && setSendOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: 20, width: "100%", maxWidth: 480, maxHeight: "85dvh", overflowY: "auto", boxShadow: "0 -8px 32px rgba(0,0,0,0.15)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>ファイルを陸上へ送る</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14 }}>PDF・PNG・JPEG を送れます（印刷依頼など）。</div>

            <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px", borderRadius: 12, border: sendFile ? "1.5px solid #86efac" : "1.5px dashed #7dd3fc", background: sendFile ? "#f0fdf4" : "#e0f2fe", color: sendFile ? "#15803d" : "#0369a1", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 12 }}>
              📎 {sendFile ? sendFile.name : "ファイルを選択（PDF/PNG/JPEG）"}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: "none" }}
                onChange={e => { const f = e.target.files?.[0]; if (f) { setSendFile(f); if (!sendTitle) setSendTitle(f.name.replace(/\.[a-z0-9]+$/i, "")); } e.target.value = ""; }} />
            </label>

            <input value={sendTitle} onChange={e => setSendTitle(e.target.value)} placeholder="タイトル（任意）"
              style={{ width: "100%", boxSizing: "border-box", padding: "12px", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 15, marginBottom: 10, outline: "none" }} />
            <input value={sendNote} onChange={e => setSendNote(e.target.value)} placeholder="メモ（任意・例: 至急印刷してください）"
              style={{ width: "100%", boxSizing: "border-box", padding: "12px", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 14, marginBottom: 14, outline: "none" }} />

            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#374151", marginBottom: 6 }}>送付先の陸上管理者（複数可・未選択なら全員）</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 16, maxHeight: 200, overflowY: "auto" }}>
              {shoreAdmins.length === 0 && <div style={{ fontSize: 13, color: "#94a3b8", padding: 6 }}>宛先候補が取得できませんでした（全員へ送信します）。</div>}
              {shoreAdmins.map(a => (
                <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 8px", borderRadius: 10, background: sendSel.includes(a.id) ? "#eef2ff" : "#f8fafc", cursor: "pointer" }}>
                  <input type="checkbox" checked={sendSel.includes(a.id)} onChange={() => setSendSel(s => s.includes(a.id) ? s.filter(x => x !== a.id) : [...s, a.id])} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{a.user_name}</span>
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setSendOpen(false)} disabled={sending} style={{ flex: 1, padding: 14, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", color: "#475569", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>キャンセル</button>
              <button onClick={doSend} disabled={sending || !sendFile} style={{ flex: 2, padding: 14, border: "none", borderRadius: 12, background: !sendFile ? "#93c5fd" : "#4338ca", color: "#fff", fontWeight: 800, fontSize: 14, cursor: sending ? "wait" : "pointer" }}>
                {sending ? "送信中…" : "陸上へ送る"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
